// Preset home-screen icons.
//
// Each preset is rendered to a 192×192 PNG data URL via a hidden <canvas>
// the first time it's needed, then cached. Rendering in JS (rather than
// shipping static PNGs) keeps the set editable without a new build, and
// feeds the same `home_icon_data_url` pipeline the uploader uses.
//
// Jun 2026 (Randy pt-home-icon-fidelity) — library expanded from 5 to 16.
// Design brief: the MAJORITY should feel creator-owned, not platform-
// branded. So most presets are monogram or abstract marks that take the
// silo's own initial; only a few carry the My Silo sparkle.
//
// Original 5 ids are preserved verbatim so icons already saved by creators
// still highlight as "selected" in the picker.

const ICON_SIZE = 192;

const PALETTE = {
  ink: "#1C1B1A",
  cream: "#FDFCF8",
  forest: "#4A5D4E",
  terracotta: "#C16E5A",
  sage: "#A7B3A0",
  // Jun 2026 — drawn from the My Silo mark (amber badge on navy).
  navy: "#222B36",
  amber: "#F8B54E",
  amberLight: "#FCD17D",
  slate: "#A7B1BB",
};

// A simple 4-pointed star (sparkle), hand-tuned inside a 100×100 box.
const SPARKLE_PATH = "M50 8 L57 43 L92 50 L57 57 L50 92 L43 57 L8 50 L43 43 Z";

function fill(ctx, color) {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, ICON_SIZE, ICON_SIZE);
}

function scaled(ctx, draw) {
  const s = ICON_SIZE / 100;
  ctx.save();
  ctx.scale(s, s);
  draw();
  ctx.restore();
}

function drawSparkleIcon(ctx, { bg, fg, border = null }) {
  fill(ctx, bg);
  if (border) {
    ctx.strokeStyle = border;
    ctx.lineWidth = 4;
    ctx.strokeRect(8, 8, ICON_SIZE - 16, ICON_SIZE - 16);
  }
  scaled(ctx, () => {
    ctx.fillStyle = fg;
    ctx.fill(new Path2D(SPARKLE_PATH));
  });
}

function drawWordmark(ctx, { bg, fg }) {
  fill(ctx, bg);
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "italic 600 56px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("My", ICON_SIZE / 2, 70);
  const letters = "SILO".split("");
  const tracking = 10;
  ctx.font = "600 36px Inter, system-ui, sans-serif";
  const widths = letters.map((l) => ctx.measureText(l).width);
  const total = widths.reduce((a, b) => a + b, 0) + tracking * (letters.length - 1);
  let x = (ICON_SIZE - total) / 2;
  ctx.textAlign = "left";
  letters.forEach((l, i) => {
    ctx.fillText(l, x, 130);
    x += widths[i] + tracking;
  });
}

// ── Creator-owned marks ────────────────────────────────────────────────
// These carry the silo's own initial, so the icon reads as the creator's
// property rather than as My Silo's.

function drawMonogram(ctx, { bg, fg, letter, ring = null, serif = true }) {
  fill(ctx, bg);
  if (ring) {
    ctx.strokeStyle = ring;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2, ICON_SIZE / 2 - 14, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = serif
    ? "600 104px 'Cormorant Garamond', Georgia, serif"
    : "600 92px Inter, system-ui, sans-serif";
  ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 6);
}

function drawGoldMonogram(ctx, { letter }) {
  fill(ctx, PALETTE.navy);
  const g = ctx.createLinearGradient(40, 40, 152, 152);
  g.addColorStop(0, PALETTE.amber);
  g.addColorStop(1, PALETTE.amberLight);
  ctx.fillStyle = g;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 108px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 6);
}

function drawOrb(ctx, { from, to, bg, letter, fg }) {
  fill(ctx, bg);
  const g = ctx.createRadialGradient(72, 64, 10, 96, 96, 108);
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2, 72, 0, Math.PI * 2);
  ctx.fill();
  if (letter) {
    ctx.fillStyle = fg;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "600 80px Inter, system-ui, sans-serif";
    ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 4);
  }
}

function drawHorizon(ctx, { sky, land, letter, fg }) {
  fill(ctx, sky);
  ctx.fillStyle = land;
  ctx.beginPath();
  ctx.moveTo(0, 118);
  ctx.quadraticCurveTo(ICON_SIZE / 2, 86, ICON_SIZE, 118);
  ctx.lineTo(ICON_SIZE, ICON_SIZE);
  ctx.lineTo(0, ICON_SIZE);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 76px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(letter, ICON_SIZE / 2, 62);
}

function drawArc(ctx, { bg, stroke, letter, fg }) {
  fill(ctx, bg);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 10;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2 + 18, 62, Math.PI * 1.08, Math.PI * 1.92);
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 72px Inter, system-ui, sans-serif";
  ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 14);
}

function drawRing(ctx, { bg, ring, letter, fg }) {
  fill(ctx, bg);
  ctx.strokeStyle = ring;
  ctx.lineWidth = 4;
  [78, 64].forEach((r) => {
    ctx.beginPath();
    ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2, r, 0, Math.PI * 2);
    ctx.stroke();
  });
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 70px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 4);
}

function drawDotGrid(ctx, { bg, dot, letter, fg }) {
  fill(ctx, bg);
  ctx.fillStyle = dot;
  for (let y = 24; y < ICON_SIZE; y += 24) {
    for (let x = 24; x < ICON_SIZE; x += 24) {
      ctx.beginPath();
      ctx.arc(x, y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2, 56, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "600 72px Inter, system-ui, sans-serif";
  ctx.fillText(letter, ICON_SIZE / 2, ICON_SIZE / 2 + 4);
}

function drawAmberSpark(ctx) {
  fill(ctx, PALETTE.navy);
  const g = ctx.createLinearGradient(44, 44, 148, 148);
  g.addColorStop(0, PALETTE.amber);
  g.addColorStop(1, PALETTE.amberLight);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(ICON_SIZE / 2, ICON_SIZE / 2, 68, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(ICON_SIZE / 2, ICON_SIZE / 2);
  ctx.scale(0.86, 0.86);
  ctx.translate(-ICON_SIZE / 2, -ICON_SIZE / 2);
  scaled(ctx, () => {
    ctx.fillStyle = "#FFFFFF";
    ctx.fill(new Path2D(SPARKLE_PATH));
  });
  ctx.restore();
  // Two small glints, upper-right — straight from the My Silo mark.
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  [[138, 52, 7], [152, 72, 5]].forEach(([cx, cy, r]) => {
    ctx.beginPath();
    ctx.moveTo(cx - r, cy - r); ctx.lineTo(cx + r, cy + r);
    ctx.moveTo(cx + r, cy - r); ctx.lineTo(cx - r, cy + r);
    ctx.stroke();
  });
}

export const PRESETS = [
  // ── Original five (ids frozen for back-compat) ──
  { id: "ink-on-cream", label: "Cream", draw: (ctx) => drawSparkleIcon(ctx, { bg: PALETTE.cream, fg: PALETTE.ink, border: PALETTE.ink }) },
  { id: "cream-on-ink", label: "Midnight", draw: (ctx) => drawSparkleIcon(ctx, { bg: PALETTE.ink, fg: PALETTE.cream }) },
  { id: "forest", label: "Forest", draw: (ctx) => drawSparkleIcon(ctx, { bg: PALETTE.forest, fg: PALETTE.cream }) },
  { id: "terracotta", label: "Terracotta", draw: (ctx) => drawSparkleIcon(ctx, { bg: PALETTE.terracotta, fg: PALETTE.cream }) },
  { id: "wordmark", label: "Wordmark", draw: (ctx) => drawWordmark(ctx, { bg: PALETTE.ink, fg: PALETTE.cream }) },

  // ── Creator-owned monograms ──
  { id: "mono-cream", label: "Initial", usesLetter: true, draw: (ctx, o) => drawMonogram(ctx, { bg: PALETTE.cream, fg: PALETTE.ink, ring: PALETTE.ink, letter: o.letter }) },
  { id: "mono-ink", label: "Initial Ink", usesLetter: true, draw: (ctx, o) => drawMonogram(ctx, { bg: PALETTE.ink, fg: PALETTE.cream, letter: o.letter }) },
  { id: "mono-forest", label: "Initial Forest", usesLetter: true, draw: (ctx, o) => drawMonogram(ctx, { bg: PALETTE.forest, fg: PALETTE.cream, ring: PALETTE.sage, letter: o.letter }) },
  { id: "mono-clay", label: "Initial Clay", usesLetter: true, draw: (ctx, o) => drawMonogram(ctx, { bg: PALETTE.terracotta, fg: PALETTE.cream, letter: o.letter }) },
  { id: "mono-sans", label: "Initial Bold", usesLetter: true, draw: (ctx, o) => drawMonogram(ctx, { bg: PALETTE.sage, fg: PALETTE.ink, letter: o.letter, serif: false }) },
  { id: "mono-gold", label: "Gold Leaf", usesLetter: true, draw: (ctx, o) => drawGoldMonogram(ctx, { letter: o.letter }) },

  // ── Abstract marks ──
  { id: "orb-forest", label: "Orb", usesLetter: true, draw: (ctx, o) => drawOrb(ctx, { bg: PALETTE.cream, from: "#6E7F6F", to: "#2F3A31", fg: PALETTE.cream, letter: o.letter }) },
  { id: "orb-clay", label: "Ember", usesLetter: true, draw: (ctx, o) => drawOrb(ctx, { bg: PALETTE.ink, from: PALETTE.amberLight, to: PALETTE.terracotta, fg: PALETTE.ink, letter: o.letter }) },
  { id: "horizon", label: "Horizon", usesLetter: true, draw: (ctx, o) => drawHorizon(ctx, { sky: PALETTE.cream, land: PALETTE.forest, fg: PALETTE.ink, letter: o.letter }) },
  { id: "arc", label: "Arc", usesLetter: true, draw: (ctx, o) => drawArc(ctx, { bg: PALETTE.ink, stroke: PALETTE.sage, fg: PALETTE.cream, letter: o.letter }) },
  { id: "ring", label: "Ring", usesLetter: true, draw: (ctx, o) => drawRing(ctx, { bg: PALETTE.cream, ring: PALETTE.forest, fg: PALETTE.forest, letter: o.letter }) },
  { id: "dot-grid", label: "Grid", usesLetter: true, draw: (ctx, o) => drawDotGrid(ctx, { bg: PALETTE.ink, dot: PALETTE.sage, fg: PALETTE.cream, letter: o.letter }) },

  // ── My Silo flavoured ──
  { id: "amber-spark", label: "Amber", draw: (ctx) => drawAmberSpark(ctx) },
];

const _cache = new Map();

export function renderPresetToDataUrl(preset, opts = {}) {
  const letter = (opts.letter || "S").slice(0, 1).toUpperCase();
  const key = preset.usesLetter ? `${preset.id}|${letter}` : preset.id;
  if (_cache.has(key)) return _cache.get(key);
  const canvas = document.createElement("canvas");
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext("2d");
  preset.draw(ctx, { letter });
  const dataUrl = canvas.toDataURL("image/png");
  _cache.set(key, dataUrl);
  return dataUrl;
}

// Derive the monogram letter from a silo name ("Randy's Studio" → "R").
export function siloLetter(name) {
  const m = (name || "").match(/[A-Za-z0-9]/);
  return (m ? m[0] : "S").toUpperCase();
}

// ── Jun 2026 — Silo artwork → icon ────────────────────────────────────
// Creators asked to reuse the artwork already behind their Public Q&A.
// Photographic art turns to mud at 60×60, so the render does three things:
// a two-step downscale (far cleaner than one big jump), an optional dark
// scrim/vignette to buy contrast, and an optional monogram so the icon
// stays legible even when the art is busy.
const SCRIMS = { none: 0, soft: 0.34, strong: 0.58 };

export function renderArtworkIcon(img, { scrim = "soft", monogram = false, letter = "S" } = {}) {
  const side = Math.min(img.width, img.height);
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;

  // Step 1: crop to a square at 2× the target, then step down to 192.
  const mid = document.createElement("canvas");
  mid.width = ICON_SIZE * 2;
  mid.height = ICON_SIZE * 2;
  const mctx = mid.getContext("2d");
  mctx.imageSmoothingEnabled = true;
  mctx.imageSmoothingQuality = "high";
  mctx.drawImage(img, sx, sy, side, side, 0, 0, mid.width, mid.height);

  const canvas = document.createElement("canvas");
  canvas.width = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(mid, 0, 0, ICON_SIZE, ICON_SIZE);

  const alpha = SCRIMS[scrim] ?? SCRIMS.soft;
  if (alpha > 0) {
    const g = ctx.createRadialGradient(
      ICON_SIZE / 2, ICON_SIZE / 2, ICON_SIZE * 0.18,
      ICON_SIZE / 2, ICON_SIZE / 2, ICON_SIZE * 0.72,
    );
    g.addColorStop(0, `rgba(12,12,14,${alpha * 0.35})`);
    g.addColorStop(1, `rgba(12,12,14,${alpha})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, ICON_SIZE, ICON_SIZE);
  }

  if (monogram) {
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "600 96px 'Cormorant Garamond', Georgia, serif";
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 14;
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(letter.slice(0, 1).toUpperCase(), ICON_SIZE / 2, ICON_SIZE / 2 + 6);
    ctx.restore();
  }

  return canvas.toDataURL("image/png");
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load that image."));
    img.src = src;
  });
}
